// frontend/src/lib/api/providerServicesService.ts
// Dedicated API service for provider services management only

import { BaseApiService } from './baseService';
import { ApiResponse } from '../httpClient';

// =============================================================================
// PROVIDER SERVICES TYPE DEFINITIONS
// =============================================================================

export interface ProviderService {
  provider_service_id: number;
  service_id: number;
  provider_id: number;
  service_type_id: number;
  service_name: string;
  description?: string;
  price_range?: string;
  estimated_duration?: string;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  // Related data
  service_type_name?: string;
  category_id?: number;
  category_name?: string;
  provider_info?: {
    business_name: string;
    business_email: string;
  };
}

export interface ProviderServiceFormData {
  service_type_id: number;
  service_name: string;
  description?: string;
  price_range?: string;
  estimated_duration?: string;
  is_active?: boolean;
  is_featured?: boolean;
}

export interface ProviderServiceUpdateData {
  service_name?: string;
  description?: string;
  price_range?: string;
  estimated_duration?: string;
  is_active?: boolean;
  is_featured?: boolean;
}

export interface ProviderServiceFilters {
  page?: number;
  limit?: number;
  search?: string;
  category_id?: number;
  service_type_id?: number;
  is_active?: boolean;
  is_featured?: boolean;
  provider_id?: number; // For admin use
}

export interface ProviderServiceStats {
  total_services: number;
  active_services: number;
  featured_services: number;
  inactive_services: number;
  by_category: Array<{
    category_name: string;
    count: number;
  }>;
}

export interface ServiceFilterOptions {
  categories: Array<{
    id: number;
    name: string;
  }>;
  service_types: Array<{
    id: number;
    name: string;
    category_id: number;
  }>;
  providers?: Array<{
    id: number;
    name: string;
  }>;
}

export interface BulkActionData {
  service_ids: number[];
  action: 'activate' | 'deactivate' | 'feature' | 'unfeature' | 'delete';
}

export interface QuickAddServiceData {
  service_type_id: number;
  service_name: string;
  price_range?: string;
  description?: string;
}

// =============================================================================
// PROVIDER SERVICES API CLASS
// =============================================================================

class ProviderServicesService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/provider/services', // Uses the provider route /api/provider/services
      timeout: 15000,
    });
  }

  // =============================================================================
  // CORE CRUD OPERATIONS
  // =============================================================================

  /**
   * Get provider services list with filtering and pagination
   * GET /api/services
   */
  async getServices(filters: ProviderServiceFilters = {}): Promise<ApiResponse<{
    services: ProviderService[];
    pagination?: any;
    stats?: ProviderServiceStats;
  }>> {
    try {
      return await this.get('', { params: filters });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch services');
    }
  }

  /**
   * Get single service by ID
   * GET /api/services/{id}
   */
  async getServiceById(id: number): Promise<ApiResponse<ProviderService>> {
    try {
      return await this.get(`/${id}`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch service');
    }
  }

  /**
   * Create new service
   * POST /api/services
   */
  async createService(data: ProviderServiceFormData): Promise<ApiResponse<ProviderService>> {
    try {
      return await this.post('', data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create service');
    }
  }

  /**
   * Update service
   * PUT /api/services/{id}
   */
  async updateService(id: number, data: ProviderServiceUpdateData): Promise<ApiResponse<ProviderService>> {
    try {
      return await this.put(`/${id}`, data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update service');
    }
  }

  /**
   * Delete service
   * DELETE /api/services/{id}
   */
  async deleteService(id: number): Promise<ApiResponse<{ message: string }>> {
    try {
      return await this.delete(`/${id}`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete service');
    }
  }

  // =============================================================================
  // SERVICE ACTIONS
  // =============================================================================

  /**
   * Toggle service status (active/inactive)
   * POST /api/services/{id}/toggle-status
   */
  async toggleServiceStatus(id: number): Promise<ApiResponse<ProviderService>> {
    try {
      return await this.post(`/${id}/toggle-status`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to toggle service status');
    }
  }

  /**
   * Toggle service featured status
   * POST /api/services/{id}/toggle-featured
   */
  async toggleServiceFeatured(id: number): Promise<ApiResponse<ProviderService>> {
    try {
      return await this.post(`/${id}/toggle-featured`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to toggle featured status');
    }
  }

  /**
   * Approve service (admin only)
   * POST /api/services/{id}/approve
   */
  async approveService(id: number): Promise<ApiResponse<ProviderService>> {
    try {
      return await this.post(`/${id}/approve`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to approve service');
    }
  }

  /**
   * Reject service (admin only)
   * POST /api/services/{id}/reject
   */
  async rejectService(id: number, reason?: string): Promise<ApiResponse<ProviderService>> {
    try {
      return await this.post(`/${id}/reject`, { reason });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to reject service');
    }
  }

  // =============================================================================
  // BROWSE AND QUICK OPERATIONS
  // =============================================================================

  /**
   * Browse available service types for adding
   * GET /api/services/browse
   */
  async browseServiceTypes(params: {
    search?: string;
    category_id?: number;
    page?: number;
    limit?: number;
  } = {}): Promise<ApiResponse<{
    service_types: Array<{
      service_type_id: number;
      name: string;
      description?: string;
      category_id: number;
      category_name: string;
      is_added_by_provider: boolean; // Whether provider already offers this service
      can_add: boolean; // Whether the service can be added (opposite of is_added_by_provider)
    }>;
    pagination?: any;
  }>> {
    try {
      return await this.get('/browse', { params });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to browse service types');
    }
  }

  /**
   * Quick add service with minimal data
   * POST /api/services/quick-add
   */
  async quickAddService(data: QuickAddServiceData): Promise<ApiResponse<ProviderService>> {
    try {
      return await this.post('/quick-add', data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to quick add service');
    }
  }

  // =============================================================================
  // BULK OPERATIONS
  // =============================================================================

  /**
   * Perform bulk actions on multiple services
   * POST /api/services/bulk-action
   */
  async bulkAction(data: BulkActionData): Promise<ApiResponse<{
    success_count: number;
    failed_count: number;
    errors?: string[];
  }>> {
    try {
      return await this.post('/bulk-action', data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to perform bulk action');
    }
  }

  // =============================================================================
  // FILTER OPTIONS AND STATS
  // =============================================================================

  /**
   * Get filter options for service management
   * GET /api/services/filter-options
   */
  async getFilterOptions(): Promise<ApiResponse<ServiceFilterOptions>> {
    try {
      return await this.get('/filter-options');
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch filter options');
    }
  }

  /**
   * Get service statistics
   * GET /api/services/stats
   */
  async getServiceStats(params: {
    provider_id?: number;
    date_from?: string;
    date_to?: string;
  } = {}): Promise<ApiResponse<ProviderServiceStats>> {
    try {
      return await this.get('/stats', { params });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch service statistics');
    }
  }

  // =============================================================================
  // EXPORT FUNCTIONALITY
  // =============================================================================

  /**
   * Export services data
   * GET /api/services/export
   */
  async exportServices(params: {
    format?: 'csv' | 'excel';
    filters?: ProviderServiceFilters;
  } = {}): Promise<ApiResponse<{
    download_url: string;
    file_name: string;
  }>> {
    try {
      return await this.get('/export', { params });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to export services');
    }
  }
}

// Create and export singleton instance
export const providerServicesService = new ProviderServicesService();
export default providerServicesService;